/*
 * Prospects (P3) in prototype mode: the sample in mock/prospects.js served in
 * the shapes the real API returns (ProspectController, ProspectResource), and
 * the shared record engines (records/prospect/:id/…) for those prospects.
 * Writes change the sample in memory until the page is reloaded.
 */
import { ok, created, fail, invalid, notFound, paginate, nextId } from "./util";
import { prospects as sample, interactionsFor } from "@/mock/prospects";
import { leadByCode } from "@/mock/sales";
import { addConvertedLead } from "./sales";
import {
  prospectStages, prospectQualification, industries, segments, leadSources, communicationTypes, taskTypes,
  locations, products, staff, teams, reasonCodes, reasonCategories, competitors, isSellable,
} from "@/mock/masters";
import { ME } from "@/mock/work";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const DAY = 86400000;
const iso = (day, time = "09:00") => (day ? new Date(`${day}T${time}:00`).toISOString() : null);
const days = (from, to = Date.now()) => (from ? Math.floor((new Date(to).setHours(0, 0, 0, 0) - new Date(from).setHours(0, 0, 0, 0)) / DAY) : null);

// ---- Masters, as GET prospects/meta returns them ---------------------------
const territoryNames = [...new Set(locations.map((l) => l.territory))];
const territories = territoryNames.map((name, i) => ({ id: i + 1, name }));
const territoryOf = (name) => territories.find((t) => t.name === name) ?? null;
const location = (id) => {
  const l = locations.find((x) => x.id === Number(id));
  return l ? { id: l.id, town: l.town, county: l.county, country: l.country, territory: territoryOf(l.territory) } : null;
};
const segmentValue = (code) => String(code || "").toLowerCase();
const SEGMENTS = segments.map((s) => ({ value: segmentValue(s.code), label: s.name, rule: s.rule }));
const label = (list, value) => list.find((x) => x.value === value)?.label ?? value;
const INDUSTRIES = industries.map((v) => ({ value: slug(v), label: v }));
const SOURCES = leadSources.map((v) => ({ value: slug(v), label: v }));
const COMMS = communicationTypes.map((v) => ({ value: slug(v), label: v }));
const TASKS = taskTypes.map((v) => ({ value: slug(v), label: v }));
const AUTO = ["contact", "need", "engagement", "owner"];
const staffRow = (id) => {
  const s = staff.find((x) => String(x.id) === String(id));
  return s ? { id: s.id, name: s.name } : null;
};
const teamRow = (id) => {
  const t = teams.find((x) => x.id === id);
  return t ? { id: t.id, name: t.name } : null;
};
const productRow = (id) => {
  const p = products.find((x) => x.id === Number(id));
  return p ? { id: p.id, code: p.code, name: p.name } : null;
};
const reasonRow = (code) => {
  const r = reasonCodes.find((x) => x.code === code);
  if (!r) return null;
  const c = reasonCategories.find((x) => x.key === r.category);
  return {
    code: r.code, name: r.name, category: r.category, category_name: c?.name, category_tone: c?.tone,
    needs_competitor: r.needs_competitor, note_required: r.code === "OTH-01", recyclable: r.recyclable,
    default_revisit_days: r.recyclable ? 90 : null, preventable: r.preventable,
  };
};
const stage = (key) => prospectStages.find((s) => s.key === key) ?? { key, name: key, kind: "open" };
const TRANSITIONS = {
  new: ["contacted", "nurture", "disqualified"], contacted: ["interested", "nurture", "disqualified"],
  interested: ["qualified", "nurture", "disqualified"], qualified: ["converted", "nurture", "disqualified"],
  nurture: ["contacted", "interested", "disqualified"],
};
const STALE_AFTER = 14;

const meta = () => ({
  stages: prospectStages.map((s) => ({ key: s.key, name: s.name, kind: s.kind, sort_order: s.order, max_days: null })),
  qualification: prospectQualification.map((q) => ({ key: q.key, label: q.label, required: q.required, auto: AUTO.includes(q.key) })),
  industries: INDUSTRIES, segments: SEGMENTS, sources: SOURCES, communication_types: COMMS, task_types: TASKS,
  locations: locations.map((l) => location(l.id)), territories,
  products: products.filter(isSellable).map((p) => ({ id: p.id, code: p.code, name: p.name })),
  staff: staff.filter((s) => s.status === "active").map((s) => ({ id: s.id, name: s.name, team_id: s.team_id, user_id: s.id })),
  reasons: reasonCodes.filter((r) => r.applies_to.includes("prospect")).map((r) => reasonRow(r.code)),
  reason_categories: reasonCategories.map((c) => ({ key: c.key, name: c.name, tone: c.tone })),
  competitors: competitors.map((c) => ({ id: c.id, name: c.name })),
  custom_fields: [
    { key: "event_name", label: "Met at event", type: "dropdown", options: ["HR Summit 2026", "SACCO Expo", "Microfinance & Lending Forum"], required: false, show_on_create: true },
    { key: "branches", label: "Number of branches", type: "number", options: null, required: false, show_on_create: true },
  ],
  settings: { stale_after_days: STALE_AFTER, pool_mode: "territory", pool_claim_limit: 25, duplicate_action: "warn", duplicate_fields: ["Organisation name", "Phone", "Email"] },
  me: { user_id: ME, staff_id: ME, team_id: staff.find((s) => s.id === ME)?.team_id ?? null },
  can: { create: true, edit: true, assign: true, reassign: true, import: true, export: true, convert: true, disqualify: true, recycle: true },
});

// ---- The sample, held in the API's own terms -------------------------------
let contactSeq = 1000;
let historySeq = 5000;
const NEXT_IDS = { task: 9000, interaction: 7000, note: 3000 };

const fromSample = (p) => {
  const owner = p.owner_id ? staff.find((s) => s.id === p.owner_id) : null;
  return {
    id: p.id, code: p.code, organisation: p.organisation,
    industry: slug(p.industry), segment: segmentValue(p.segment), staff_count: p.staff_count, location_id: p.location_id,
    source: slug(p.source), campaign: p.campaign, owner_id: p.owner_id ?? null, team_id: owner?.team_id ?? null,
    stage: p.stage, stage_entered_at: iso(p.history[0]?.at ?? p.created_at),
    contacts: [{ id: contactSeq++, name: p.contact_name, title: p.contact_title, phone: p.phone, email: p.email, is_primary: true, is_decision_maker: false }],
    estimated_value: p.estimated_value, product_ids: [...p.product_ids],
    qualification: { ...p.qualification }, notes: {}, need_notes: p.product_ids.length ? "Automate payroll and statutory returns; leave tracking for 3 branches." : null,
    decision_maker_notes: `${p.contact_name} (${p.contact_title})`,
    last_contact_at: iso(p.last_contact_at, "10:30"),
    next_action: p.next_action ? { id: NEXT_IDS.task++, type: slug(p.next_action.type), due_at: iso(p.next_action.date, "10:00"), note: p.next_action.note, owner_id: p.owner_id } : null,
    done_tasks: p.last_contact_at ? [
      { id: NEXT_IDS.task++, type: "email", title: "Send brochure", due_at: iso(new Date(Date.now() - 8 * DAY).toISOString().slice(0, 10)), status: "done", owner: "Jane Wanjiku" },
      { id: NEXT_IDS.task++, type: "meeting", title: "Intro meeting", due_at: iso(new Date(Date.now() - 12 * DAY).toISOString().slice(0, 10)), status: "done", owner: "Brian Otieno" },
    ] : [],
    score: p.score, is_strategic: p.segment === "STR",
    outcome: p.failed ? {
      reason_code: p.failed.code, stage_key: p.failed.at, note: p.failed.note, competitor: p.failed.competitor ?? null,
      revisit_on: p.failed.revisit_on, recorded_at: iso(p.failed.on), recorded_by: p.failed.by, recycled_at: null,
    } : null,
    converted: p.converted_lead ? { lead_id: leadByCode(p.converted_lead)?.id ?? 1, lead_code: p.converted_lead, at: iso(p.history[0]?.at) } : null,
    history: [
      ...p.history.map((h) => ({ id: historySeq++, from: h.from, to: h.to, at: iso(h.at), by: "Jane Wanjiku", days_in_previous: h.days_in_previous, reason_code: h.reason ?? null, note: h.reason ? p.failed?.note : null })),
      { id: historySeq++, from: null, to: "new", at: iso(p.created_at, "08:00"), by: p.source === "Website" ? "Website form" : "Jane Wanjiku", days_in_previous: null, reason_code: null, note: `Created from ${p.source}` },
    ],
    interactions: interactionsFor(p).map((i) => ({ id: NEXT_IDS.interaction++, type: slug(i.type), subject: i.title, summary: i.body, occurred_at: new Date(i.at).toISOString(), by: i.by })),
    noteList: [],
    attachments: p.last_contact_at ? [{ id: 1, name: "Company profile.pdf", visibility: "internal", document_type: { id: 4, code: "PROPOSAL", name: "Proposal" }, mime_type: "application/pdf", size_bytes: 2_200_000, ready: true, uploaded_by: "Jane Wanjiku", mine: true, created_at: iso(p.created_at) }] : [],
    assignments: owner ? [{ id: historySeq++, from: null, to: owner.name, to_staff_id: owner.id, team: teamRow(owner.team_id)?.name, method: "manual", reason: null, by: "Brian Otieno", at: iso(p.created_at, "08:40") }] : [],
    pool_reason: p.owner_id ? null : "new",
    recycled_count: 0, duplicate_override_reason: null,
    created_by_name: "Jane Wanjiku",
    created_at: iso(p.created_at, "08:00"), updated_at: iso(p.last_contact_at ?? p.created_at, "10:30"),
  };
};

const records = sample.map(fromSample);
const find = (id) => records.find((r) => String(r.id) === String(id));

const isOpen = (r) => ["open", "paused"].includes(stage(r.stage).kind);
const primary = (r) => r.contacts.find((c) => c.is_primary) ?? r.contacts[0] ?? null;
const autoChecks = (r) => ({
  contact: !!(primary(r)?.name && (primary(r)?.phone || primary(r)?.email)),
  need: r.product_ids.length > 0 || !!r.need_notes,
  engagement: r.interactions.length > 0,
  owner: !!r.owner_id,
});
const checked = (r, key) => (AUTO.includes(key) ? autoChecks(r)[key] : !!r.qualification[key]);
const priorityOf = (score) => (score === null || score === undefined ? null : score >= 70 ? "high" : score >= 40 ? "medium" : "low");

const nextActionOf = (r) => r.next_action && isOpen(r) ? {
  id: r.next_action.id, type: r.next_action.type, type_label: label(TASKS, r.next_action.type), due_at: r.next_action.due_at,
  note: r.next_action.note, overdue: new Date(r.next_action.due_at) < new Date(), owner: staffRow(r.next_action.owner_id ?? r.owner_id),
} : null;

const outcomeOf = (r) => {
  if (!r.outcome) return null;
  const reason = reasonRow(r.outcome.reason_code);
  return {
    ...r.outcome, reason: reason?.name, category: reason?.category, category_name: reason?.category_name, category_tone: reason?.category_tone,
    stage_name: stage(r.outcome.stage_key).name, recyclable: !!reason?.recyclable, preventable: !!reason?.preventable,
  };
};

const row = (r) => {
  const required = prospectQualification.filter((q) => q.required);
  const done = required.filter((q) => checked(r, q.key)).length;
  const pc = primary(r);
  const na = nextActionOf(r);
  const sinceContact = days(r.last_contact_at ?? r.created_at);
  return {
    id: r.id, code: r.code, organisation: r.organisation,
    industry: r.industry, industry_label: label(INDUSTRIES, r.industry), segment: r.segment, segment_label: label(SEGMENTS, r.segment),
    staff_count: r.staff_count, location: location(r.location_id),
    source: r.source, source_label: label(SOURCES, r.source), campaign: r.campaign,
    owner: staffRow(r.owner_id), team: teamRow(r.team_id), in_pool: !r.owner_id && isOpen(r),
    stage: r.stage, stage_name: stage(r.stage).name, stage_kind: stage(r.stage).kind, stage_entered_at: r.stage_entered_at,
    primary_contact: pc ? { id: pc.id, name: pc.name, title: pc.title, phone: pc.phone, email: pc.email } : null,
    estimated_value: r.estimated_value, estimated_value_masked: false,
    products: r.product_ids.map(productRow).filter(Boolean),
    qualification: { done, of: required.length, percent: required.length ? Math.round((done / required.length) * 100) : 100 },
    last_contact_at: r.last_contact_at, next_action: na, needs_next_action: isOpen(r) && !na,
    score: r.score, priority: priorityOf(r.score),
    ageing: {
      days_old: days(r.created_at), days_since_contact: r.last_contact_at ? days(r.last_contact_at) : null,
      days_to_next_action: na ? -days(na.due_at) : null, days_in_stage: days(r.stage_entered_at),
      stale: isOpen(r) && sinceContact > STALE_AFTER,
    },
    converted: r.converted, outcome: outcomeOf(r), locked: !!r.converted,
    created_at: r.created_at, updated_at: r.updated_at,
  };
};

const breakdown = (r) => {
  const parts = [
    ["fit", "Fit (segment, industry, size)", 30, { str: 100, ent: 90, mid: 70, sme: 40 }[r.segment] ?? 50, [`${label(SEGMENTS, r.segment)} segment`, `${r.staff_count ?? "?"} staff`]],
    ["engagement", "Engagement", 25, Math.min(100, r.interactions.length * 30 + (r.last_contact_at && days(r.last_contact_at) <= 7 ? 20 : 0)), [`${r.interactions.length} interaction(s)`, r.last_contact_at ? `last contact ${days(r.last_contact_at)} days ago` : "never contacted"]],
    ["timing", "Timing", 15, { new: 10, contacted: 40, interested: 70, qualified: 100, nurture: 30 }[r.stage] ?? 0, [`In ${stage(r.stage).name}`]],
    ["need", "Need / product interest", 20, Math.min(100, r.product_ids.length * 60 + (r.need_notes ? 40 : 0)), [`${r.product_ids.length} product(s) of interest`]],
    ["strategic", "Strategic account", 10, r.is_strategic ? 100 : 0, [r.is_strategic ? "Flagged strategic" : "Not flagged"]],
  ];
  return parts.map(([key, lbl, weight, points, why]) => ({ key, label: lbl, weight, points, contribution: Math.round((weight * points) / 100), why }));
};

const detail = (r) => {
  const allowed = isOpen(r) ? (TRANSITIONS[r.stage] ?? []) : [];
  const checks = [
    { key: "contact", label: "Contact details (name and phone or email)", ok: autoChecks(r).contact },
    { key: "need", label: "Product or need identified", ok: autoChecks(r).need },
    { key: "engagement", label: "At least one logged interaction", ok: autoChecks(r).engagement },
    { key: "owner", label: "Owner assigned", ok: autoChecks(r).owner },
    ...prospectQualification.filter((q) => q.required && !AUTO.includes(q.key)).map((q) => ({ key: q.key, label: q.label, ok: checked(r, q.key) })),
  ];
  return {
    ...row(r),
    contacts: r.contacts.map((c) => ({ ...c })),
    qualification_items: prospectQualification.map((q) => ({
      key: q.key, label: q.label, required: q.required, checked: checked(r, q.key), auto: AUTO.includes(q.key),
      note: r.notes[q.key] ?? null, checked_by: checked(r, q.key) && !AUTO.includes(q.key) ? "Jane Wanjiku" : null, checked_at: null,
    })),
    need_notes: r.need_notes, decision_maker_notes: r.decision_maker_notes, custom: r.custom ?? {}, is_strategic: r.is_strategic,
    score_breakdown: breakdown(r),
    readiness: { ready: checks.every((c) => c.ok), checks, missing: checks.filter((c) => !c.ok).map((c) => c.label) },
    history: r.history.map((h) => ({ ...h, from_name: h.from ? stage(h.from).name : null, to_name: stage(h.to).name, reason: h.reason_code ? reasonRow(h.reason_code)?.name : null })),
    allowed_stages: allowed.map((k) => ({ key: k, name: stage(k).name, kind: stage(k).kind })),
    recycled_count: r.recycled_count, pool_reason: r.pool_reason, created_by_name: r.created_by_name,
    duplicate_override_reason: r.duplicate_override_reason,
    can: {
      edit: !r.converted, assign: true, reassign: true, convert: isOpen(r), disqualify: isOpen(r),
      recycle: r.stage === "disqualified" && !!outcomeOf(r)?.recyclable, claim: !r.owner_id && isOpen(r), delete: !r.interactions.length,
    },
  };
};

// ---- Lists ------------------------------------------------------------------
const myTeam = () => staff.filter((s) => s.team_id === staff.find((x) => x.id === ME)?.team_id).map((s) => s.id);
const list = (q, { withStage = true } = {}) => records.filter((r) => {
  const v = row(r);
  if (q.view === "mine" && r.owner_id !== ME) return false;
  if (q.view === "team" && !myTeam().includes(r.owner_id)) return false;
  const stages = [].concat(q.stage ?? q["stage[]"] ?? []).filter(Boolean);
  if (withStage && stages.length && !stages.includes(r.stage)) return false;
  if (q.owner_id === "none" && r.owner_id) return false;
  if (q.owner_id && q.owner_id !== "none" && String(r.owner_id) !== String(q.owner_id)) return false;
  if (q.source && r.source !== q.source) return false;
  if (q.industry && r.industry !== q.industry) return false;
  if (q.segment && r.segment !== q.segment) return false;
  if (q.territory_id && String(v.location?.territory?.id) !== String(q.territory_id)) return false;
  if (q.location_id && String(r.location_id) !== String(q.location_id)) return false;
  if (q.priority && v.priority !== q.priority) return false;
  if (q.campaign && !String(r.campaign ?? "").toLowerCase().includes(String(q.campaign).toLowerCase())) return false;
  if (truthy(q.overdue) && !v.next_action?.overdue) return false;
  if (truthy(q.stale) && !v.ageing.stale) return false;
  if (truthy(q.without_next_action) && !v.needs_next_action) return false;
  if (q.created_from && r.created_at < q.created_from) return false;
  if (q.created_to && r.created_at.slice(0, 10) > q.created_to) return false;
  const s = String(q.search ?? "").trim().toLowerCase();
  return !s || [r.organisation, r.code, ...r.contacts.flatMap((c) => [c.name, c.email, c.phone])].some((x) => String(x ?? "").toLowerCase().includes(s));
});
const truthy = (v) => [true, 1, "1", "true"].includes(v);
const resourcePage = (items, q) => {
  const { data, ...p } = paginate(items, q, 25);
  return { data, links: {}, meta: { current_page: p.current_page, from: p.from, last_page: p.last_page, per_page: p.per_page, to: p.to, total: p.total } };
};
const sortRows = (rows, q) => {
  const by = q.sort_by || "created_at";
  const dir = q.sort_dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => (String(a[by] ?? "") > String(b[by] ?? "") ? dir : -dir));
};

// ---- Duplicates ---------------------------------------------------------------
const norm = (s) => String(s ?? "").toLowerCase().replace(/\b(ltd|limited|plc|group|the|co|company)\b/g, "").replace(/[^a-z0-9]/g, "");
const phoneNorm = (s) => {
  let d = String(s ?? "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) return d;
  if (d.startsWith("0")) d = d.slice(1);
  if (d.length === 9) return `+254${d}`;
  return d.startsWith("254") ? `+${d}` : d;
};
const duplicates = ({ organisation = "", email = "", phone = "", exclude_id = null }) => {
  const org = norm(organisation);
  const mail = String(email ?? "").trim().toLowerCase();
  const tel = phoneNorm(phone);
  return records.filter((r) => String(r.id) !== String(exclude_id)).map((r) => {
    const reasons = [];
    const o = norm(r.organisation);
    if (org.length > 3 && (o === org || o.includes(org) || org.includes(o))) reasons.push("organisation");
    if (mail && r.contacts.some((c) => String(c.email ?? "").toLowerCase() === mail)) reasons.push("email");
    if (tel.length > 8 && r.contacts.some((c) => phoneNorm(c.phone) === tel)) reasons.push("phone");
    if (!reasons.length) return null;
    return {
      type: "prospect", id: r.id, code: r.code, organisation: r.organisation, contact: primary(r)?.name ?? null,
      owner: staffRow(r.owner_id)?.name ?? null, stage_name: stage(r.stage).name, reasons, similarity: reasons.includes("organisation") ? (o === org ? 1 : 0.7) : null,
    };
  }).filter(Boolean);
};

// ---- Writes ---------------------------------------------------------------
const today = () => new Date().toISOString();
const move = (r, to, data = {}) => {
  const enteredDays = days(r.stage_entered_at) ?? 0;
  r.history.unshift({ id: historySeq++, from: r.stage, to, at: today(), by: "Jane Wanjiku", days_in_previous: enteredDays, reason_code: data.reason_code ?? null, note: data.note ?? null });
  r.stage = to;
  r.stage_entered_at = today();
  r.updated_at = today();
};
const nextCode = () => `PRS-2026-${String(records.length + 101).padStart(4, "0")}`;
const changeStage = (r, to, body = {}) => {
  if (!r) return notFound("That prospect does not exist.");
  if (r.converted) return fail(`${r.code} was converted to ${r.converted.lead_code}; it is locked.`, null, 409);
  const allowed = TRANSITIONS[r.stage] ?? [];
  if (!allowed.includes(to)) {
    return fail(`${stage(r.stage).name} → ${stage(to).name} is not an allowed move.`, { code: "CRM_020", details: { allowed: allowed.map((k) => ({ key: k, name: stage(k).name })) } }, 422);
  }
  move(r, to, body);
  return null;
};

const timeline = (r) => {
  const items = [
    ...r.history.map((h) => ({ kind: "stage", id: `s${h.id}`, at: h.at, title: h.from ? `${stage(h.from).name} → ${stage(h.to).name}` : `Created in ${stage(h.to).name}`, body: h.note, by: h.by, tone: "primary", icon: "iconoir-arrow-right", meta: { from: h.from, to: h.to, reason_code: h.reason_code } })),
    ...r.interactions.map((i) => ({ kind: "interaction", id: `i${i.id}`, at: i.occurred_at, title: i.subject || label(COMMS, i.type), body: i.summary, by: i.by, tone: "info", icon: null, meta: { type: i.type } })),
    ...r.noteList.map((n) => ({ kind: "note", id: `n${n.id}`, at: n.created_at, title: "Note", body: n.body, by: n.author, tone: "secondary", icon: "iconoir-edit-pencil", meta: { visibility: n.visibility } })),
    ...r.assignments.map((a) => ({ kind: "assignment", id: `a${a.id}`, at: a.at, title: a.to ? `Assigned to ${a.to}` : "Back in the pool", body: a.reason, by: a.by, tone: "primary", icon: "iconoir-user", meta: { method: a.method } })),
    ...(r.next_action ? [{ kind: "task", id: `t${r.next_action.id}`, at: r.updated_at, title: `Next action: ${label(TASKS, r.next_action.type)}`, body: r.next_action.note, by: "Jane Wanjiku", tone: "warning", icon: "iconoir-calendar", meta: { status: "open", due_at: r.next_action.due_at, is_follow_up: true, type: r.next_action.type } }] : []),
    ...r.done_tasks.map((t) => ({ kind: "task", id: `t${t.id}`, at: t.due_at, title: `Done: ${t.title}`, body: null, by: t.owner, tone: "success", icon: "iconoir-check", meta: { status: "done", due_at: t.due_at, type: t.type } })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const counts = items.reduce((m, i) => ({ ...m, [i.kind]: (m[i.kind] ?? 0) + 1 }), {});
  return { record: { type: "prospect", id: r.id, code: r.code, name: r.organisation, label: "Prospect", route: "prospects.show" }, items, has_more: false, counts };
};

const setNextAction = (r, na) => {
  r.next_action = { id: NEXT_IDS.task++, type: na.type, due_at: String(na.due_at).length <= 10 ? iso(na.due_at, "10:00") : na.due_at, note: na.note ?? null, owner_id: na.owner_id ?? r.owner_id };
};

const dropOff = () => {
  const failed = records.filter((r) => r.stage === "disqualified" && r.outcome);
  const rows = failed.map(outcomeOf);
  const openStages = prospectStages.filter((s) => s.kind === "open");
  return {
    totals: {
      disqualified: failed.length, share: records.length ? Math.round((failed.length / records.length) * 1000) / 10 : 0,
      preventable: rows.filter((o) => o.preventable).length, recyclable: rows.filter((o) => o.recyclable).length,
      due: rows.filter((o) => o.recyclable && o.revisit_on && o.revisit_on <= today().slice(0, 10)).length,
    },
    by_category: reasonCategories.map((c) => ({ key: c.key, name: c.name, tone: c.tone, count: rows.filter((o) => o.category === c.key).length })).filter((c) => c.count),
    by_stage: openStages.map((s) => ({ key: s.key, name: s.name, count: rows.filter((o) => o.stage_key === s.key).length })),
    matrix: openStages.flatMap((s) => reasonCategories.map((c) => ({ stage: s.key, category: c.key, count: rows.filter((o) => o.stage_key === s.key && o.category === c.key).length })).filter((m) => m.count)),
  };
};

const suggestions = (r) => {
  const sales = staff.filter((s) => s.status === "active" && [1, 2, 3].includes(s.team_id));
  const owners = sales.map((s) => {
    const open = records.filter((x) => x.owner_id === s.id && isOpen(x)).length;
    const won = records.filter((x) => x.owner_id === s.id && x.converted).length;
    const mine = records.filter((x) => x.owner_id === s.id).length;
    const terr = location(r.location_id)?.territory?.name;
    const match = teamRow(s.team_id)?.name === terr;
    const rate = mine ? Math.round((won / mine) * 100) : 0;
    const score = (match ? 40 : 10) + Math.max(0, 30 - open * 4) + Math.round(rate * 0.3);
    return { id: s.id, name: s.name, team: teamRow(s.team_id)?.name ?? null, open, conversion_rate: rate, territory_match: match, score };
  }).sort((a, b) => b.score - a.score).slice(0, 3).map((o, i) => ({ ...o, fit: o.open > 6 ? "Busy" : i === 0 ? "Best fit" : "Good" }));
  const na = nextActionOf(r);
  const next = !r.interactions.length
    ? { type: "call", note: `Introductory call with ${primary(r)?.name ?? "the contact"}`, why: "Nobody has spoken to them yet." }
    : na?.overdue ? { type: na.type, note: na.note, why: "The current next action is overdue: do it first." }
      : detail(r).readiness.ready && r.stage === "qualified" ? { type: "meeting", note: "Agree scope and convert to a lead", why: "Everything needed to convert is in place." }
        : { type: "meeting", note: "Confirm the decision maker and product fit", why: "Qualification items are still open." };
  const due = new Date(Date.now() + 2 * DAY).toISOString();
  return { heuristic: true, owners, next_action: { ...next, type_label: label(TASKS, next.type), due_at: due } };
};

const views = [{ id: 1, name: "My overdue follow-ups", filters: { view: "mine", overdue: true }, is_shared: false, mine: true }];

export default [
  ["get", "tenant/prospects/meta", () => ok(meta())],
  ["get", "tenant/prospects/summary", ({ query }) => {
    const rows = list(query, { withStage: false });
    const total = rows.length;
    return ok({
      total,
      funnel: prospectStages.map((s) => {
        const count = rows.filter((r) => r.stage === s.key).length;
        return { key: s.key, name: s.name, kind: s.kind, count, share: total ? Math.round((count / total) * 1000) / 10 : 0 };
      }),
      counts: {
        overdue: rows.filter((r) => row(r).next_action?.overdue).length, stale: rows.filter((r) => row(r).ageing.stale).length,
        pool: rows.filter((r) => row(r).in_pool).length, without_next_action: rows.filter((r) => row(r).needs_next_action).length,
        unassigned: rows.filter((r) => !r.owner_id).length,
      },
    });
  }],
  ["get", "tenant/prospects/pool", ({ query }) => {
    const rows = records.filter((r) => isOpen(r) && !r.owner_id)
      .filter((r) => !query.territory_id || String(location(r.location_id)?.territory?.id) === String(query.territory_id))
      .map((r) => ({ ...row(r), pool_reason: r.pool_reason ?? "new", pool_reason_label: { new: "New, unassigned", recycled: "Recycled", import: "Imported", unrouted: "No routing rule" }[r.pool_reason ?? "new"], waiting_days: days(r.updated_at) }));
    return ok(resourcePage(rows, query));
  }],
  ["get", "tenant/prospects/disqualified", ({ query }) => {
    const rows = records.filter((r) => r.stage === "disqualified" && r.outcome).map(row)
      .filter((v) => !query.category || v.outcome.category === query.category)
      .filter((v) => !query.stage || v.outcome.stage_key === query.stage)
      .filter((v) => !truthy(query.due) || (v.outcome.recyclable && v.outcome.revisit_on && v.outcome.revisit_on <= today().slice(0, 10)));
    return ok(resourcePage(rows, query));
  }],
  ["get", "tenant/prospects/reports/drop-off", () => ok(dropOff())],
  ["get", "tenant/prospects/reports/funnel", () => {
    const path = prospectStages.filter((s) => s.kind !== "lost" && s.kind !== "paused");
    const reached = (key) => records.filter((r) => r.history.some((h) => h.to === key)).length;
    const stages = path.map((s, i) => ({ key: s.key, name: s.name, kind: s.kind, current: records.filter((r) => r.stage === s.key).length, reached: reached(s.key), rate_from_previous: i ? Math.round((reached(s.key) / Math.max(1, reached(path[i - 1].key))) * 1000) / 10 : 100 }));
    const converted = records.filter((r) => r.converted).length;
    return ok({ stages, total: records.length, converted, disqualified: records.filter((r) => r.stage === "disqualified").length, conversion_rate: Math.round((converted / records.length) * 1000) / 10 });
  }],
  ["get", "tenant/prospects/reports/sources", () => {
    const group = (key) => {
      const keys = [...new Set(records.map((r) => r[key]).filter(Boolean))];
      return keys.map((k) => {
        const rs = records.filter((r) => r[key] === k);
        const conv = rs.filter((r) => r.converted).length;
        return { [key]: k, label: key === "source" ? label(SOURCES, k) : k, prospects: rs.length, open: rs.filter(isOpen).length, converted: conv, disqualified: rs.filter((r) => r.stage === "disqualified").length, conversion_rate: Math.round((conv / rs.length) * 1000) / 10, avg_days_to_convert: conv ? 30 : null };
      });
    };
    return ok({ by_source: group("source"), by_campaign: group("campaign") });
  }],
  ["get", "tenant/prospects/views", () => ok(views)],
  ["post", "tenant/prospects/views", ({ body }) => {
    if (!body.name?.trim()) return invalid({ name: ["Give the view a name."] });
    const v = { id: nextId(views), name: body.name, filters: body.filters ?? {}, is_shared: !!body.is_shared, mine: true };
    views.push(v);
    return created(v, "View saved.");
  }],
  ["delete", "tenant/prospects/views/:id", ({ params }) => {
    const i = views.findIndex((v) => String(v.id) === String(params.id));
    if (i >= 0) views.splice(i, 1);
    return ok(null, "View removed.");
  }],
  ["post", "tenant/prospects/check-duplicates", ({ body }) => {
    const matches = duplicates(body);
    return ok({ count: matches.length, matches });
  }],
  ["post", "tenant/prospects/reassign", ({ body }) => {
    const to = staffRow(body.to_staff_id);
    if (!to) return invalid({ to_staff_id: ["Choose who they go to."] });
    if (!body.ids?.length) return invalid({ ids: ["Choose the prospects to move."] });
    return ok({ id: 1, reference: "RAB-2026-000014", type: "prospect", count: body.ids.length, to, status: "pending", approval: { id: 1, reference: "APR-2026-000913", status: "pending" } },
      `RAB-2026-000014 sent for approval; nothing moves until it is approved.`, 202);
  }],
  ["get", "tenant/prospects", ({ query }) => ok(resourcePage(sortRows(list(query).map(row), query), query))],
  ["post", "tenant/prospects", ({ body }) => {
    const errors = {};
    if (!body.organisation?.trim()) errors.organisation = ["The organisation name is required."];
    if (!body.contact?.name?.trim()) errors["contact.name"] = ["The contact's name is required."];
    if (!body.contact?.phone?.trim() && !body.contact?.email?.trim()) errors["contact.phone"] = ["Give a phone number or an email."];
    if (!body.industry) errors.industry = ["Choose the industry."];
    if (!body.location_id) errors.location_id = ["Choose where they are."];
    if (!body.source) errors.source = ["Choose the source."];
    if (Object.keys(errors).length) return invalid(errors);
    const matches = duplicates({ organisation: body.organisation, email: body.contact?.email, phone: body.contact?.phone });
    if (matches.length && !body.confirm_not_duplicate) {
      return fail(`This looks like ${matches[0].code} ${matches[0].organisation}. Open it, or confirm it is a different organisation.`, { code: "CRM_002", details: { matches } }, 409);
    }
    if (matches.length && !body.duplicate_reason?.trim()) return invalid({ duplicate_reason: ["Say why it is not a duplicate; it is kept in the audit trail."] });
    const owner = body.owner_id ? staff.find((s) => String(s.id) === String(body.owner_id)) : null;
    const r = {
      id: nextId(records), code: nextCode(), organisation: body.organisation.trim(), industry: body.industry, segment: body.segment || null,
      staff_count: body.staff_count || null, location_id: Number(body.location_id), source: body.source, campaign: body.campaign || null,
      owner_id: owner?.id ?? null, team_id: owner?.team_id ?? null, stage: "new", stage_entered_at: today(),
      contacts: [{ id: contactSeq++, name: body.contact.name, title: body.contact.title ?? null, phone: body.contact.phone ?? null, email: body.contact.email ?? null, is_primary: true, is_decision_maker: false }],
      estimated_value: body.estimated_value || null, product_ids: (body.product_ids ?? []).map(Number), qualification: {}, notes: {},
      need_notes: body.need_notes ?? null, decision_maker_notes: null, last_contact_at: null, next_action: null, done_tasks: [],
      score: 35, is_strategic: !!body.is_strategic, outcome: null, converted: null,
      history: [{ id: historySeq++, from: null, to: "new", at: today(), by: "Jane Wanjiku", days_in_previous: null, reason_code: null, note: "Created by hand" }],
      interactions: [], noteList: [], attachments: [],
      assignments: owner ? [{ id: historySeq++, from: null, to: owner.name, to_staff_id: owner.id, team: teamRow(owner.team_id)?.name, method: "manual", reason: null, by: "Jane Wanjiku", at: today() }] : [],
      pool_reason: owner ? null : body.assignment === "pool" ? "new" : "unrouted", recycled_count: 0,
      duplicate_override_reason: matches.length ? body.duplicate_reason : null, custom: body.custom ?? {},
      created_by_name: "Jane Wanjiku", created_at: today(), updated_at: today(),
    };
    if (body.next_action?.type && body.next_action?.due_at) setNextAction(r, body.next_action);
    records.unshift(r);
    return created(detail(r), owner ? `${r.code} created and given to ${owner.name}.` : `${r.code} created; it is in the pool.`);
  }],
  ["get", "tenant/prospects/:id", ({ params }) => (find(params.id) ? ok(detail(find(params.id))) : notFound("That prospect does not exist."))],
  ["put", "tenant/prospects/:id", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (r.converted) return fail(`${r.code} was converted to ${r.converted.lead_code}; it is locked.`, null, 409);
    ["organisation", "industry", "segment", "staff_count", "source", "campaign", "estimated_value", "need_notes", "is_strategic"].forEach((k) => body[k] !== undefined && (r[k] = body[k]));
    if (body.location_id) r.location_id = Number(body.location_id);
    if (body.product_ids) r.product_ids = body.product_ids.map(Number);
    r.updated_at = today();
    return ok(detail(r), "Saved.");
  }],
  ["delete", "tenant/prospects/:id", ({ params }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (r.interactions.length || r.history.length > 1) return fail(`${r.code} has activity on it, so it cannot be deleted: disqualify it instead.`, { code: "CRM_004" }, 409);
    records.splice(records.indexOf(r), 1);
    return ok(null, `${r.code} deleted.`);
  }],
  ["put", "tenant/prospects/:id/qualification", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (!isOpen(r)) return fail("Only an open prospect's qualification can change.", null, 409);
    Object.entries(body.items ?? {}).forEach(([k, v]) => !AUTO.includes(k) && (r.qualification[k] = !!v));
    Object.assign(r.notes, body.notes ?? {});
    if (body.need_notes !== undefined) r.need_notes = body.need_notes;
    if (body.decision_maker_notes !== undefined) r.decision_maker_notes = body.decision_maker_notes;
    return ok(detail(r), "Qualification saved.");
  }],
  ["post", "tenant/prospects/:id/interactions", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (r.converted) return fail(`${r.code} was converted to ${r.converted.lead_code}; it is locked.`, null, 409);
    if (!body.type) return invalid({ type: ["Choose how it happened."] });
    if (isOpen(r) && !nextActionOf(r) && !(body.next_action?.type && body.next_action?.due_at)) {
      return invalid({ next_action: ["An open prospect always keeps a next action: say what happens next and when."] });
    }
    const i = { id: NEXT_IDS.interaction++, type: body.type, subject: body.subject ?? null, summary: body.summary ?? null, occurred_at: body.occurred_at ?? today(), by: "Jane Wanjiku" };
    r.interactions.unshift(i);
    r.last_contact_at = i.occurred_at;
    if (body.next_action?.type && body.next_action?.due_at) setNextAction(r, body.next_action);
    r.updated_at = today();
    return created({ interaction: { ...i, type_label: label(COMMS, i.type), logged_by: i.by }, prospect: detail(r) }, body.next_action ? "Logged, and the next action set." : "Logged.");
  }],
  ["post", "tenant/prospects/:id/contacts", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (!body.name?.trim()) return invalid({ name: ["The contact's name is required."] });
    if (body.is_primary) r.contacts.forEach((c) => (c.is_primary = false));
    r.contacts.push({ id: contactSeq++, name: body.name, title: body.title ?? null, phone: body.phone ?? null, email: body.email ?? null, is_primary: !!body.is_primary, is_decision_maker: !!body.is_decision_maker });
    return created(detail(r), "Contact added.");
  }],
  ["put", "tenant/prospects/:id/contacts/:contactId", ({ params, body }) => {
    const r = find(params.id);
    const c = r?.contacts.find((x) => String(x.id) === String(params.contactId));
    if (!c) return notFound("That contact does not exist.");
    if (body.is_primary) r.contacts.forEach((x) => (x.is_primary = false));
    Object.assign(c, body);
    return ok(detail(r), "Contact saved.");
  }],
  ["delete", "tenant/prospects/:id/contacts/:contactId", ({ params }) => {
    const r = find(params.id);
    const c = r?.contacts.find((x) => String(x.id) === String(params.contactId));
    if (!c) return notFound("That contact does not exist.");
    if (c.is_primary) return fail("The primary contact cannot be removed; make somebody else primary first.", null, 409);
    r.contacts.splice(r.contacts.indexOf(c), 1);
    return ok(detail(r), "Contact removed.");
  }],
  ["post", "tenant/prospects/:id/convert", ({ params }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (r.converted) return fail(`${r.code} was already converted to ${r.converted.lead_code}; a prospect converts once.`, { code: "CRM_004" }, 409);
    const d = detail(r);
    if (!d.readiness.ready) return fail(`Not ready to convert. Still needed: ${d.readiness.missing.join("; ")}.`, { code: "CRM_020", details: { missing: d.readiness.missing, checks: d.readiness.checks } }, 422);
    if (r.stage === "interested") move(r, "qualified", { note: "Qualified on conversion" });
    if (r.stage !== "qualified") return fail(`${stage(r.stage).name} → Converted to Lead is not an allowed move.`, { code: "CRM_020", details: { allowed: (TRANSITIONS[r.stage] ?? []).map((k) => ({ key: k, name: stage(k).name })) } }, 422);
    const lead = addConvertedLead({ ...r, code: r.code });
    const code = lead.code;
    move(r, "converted", { note: `Converted to lead ${code}` });
    r.converted = { lead_id: lead.id, lead_code: code, at: today() };
    r.next_action = null;
    return created({ lead: { id: lead.id, code, stage: "new", organisation: lead.organisation }, prospect: detail(r) }, `Lead ${code} created. The prospect is now locked.`);
  }],
  ["post", "tenant/prospects/:id/disqualify", ({ params, body }) => {
    const r = find(params.id);
    const reason = reasonRow(body.reason_code);
    if (!reason) return invalid({ reason_code: ["Choose why: a reason code is required."] });
    if (reason.needs_competitor && !body.competitor_id) return invalid({ competitor_id: [`${reason.code} needs the competitor it was lost to.`] });
    if (!body.note?.trim()) return invalid({ note: ["Before it moves to Disqualified, fill in: Note."] });
    const from = r?.stage;
    const error = changeStage(r, "disqualified", body);
    if (error) return error;
    r.outcome = {
      reason_code: reason.code, stage_key: from, note: body.note, competitor: competitors.find((c) => String(c.id) === String(body.competitor_id))?.name ?? null,
      revisit_on: reason.recyclable ? (body.revisit_on || new Date(Date.now() + (reason.default_revisit_days ?? 90) * DAY).toISOString().slice(0, 10)) : null,
      recorded_at: today(), recorded_by: "Jane Wanjiku", recycled_at: null,
    };
    r.next_action = null;
    return ok(detail(r), "Disqualified. The reason is recorded for the drop-off report.");
  }],
  ["post", "tenant/prospects/:id/recycle", ({ params }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (r.stage !== "disqualified" || !outcomeOf(r)?.recyclable) return fail("Only a prospect disqualified for a recyclable reason can be recycled.", null, 409);
    r.outcome.recycled_at = today();
    r.history.unshift({ id: historySeq++, from: "disqualified", to: "new", at: today(), by: "Jane Wanjiku", days_in_previous: days(r.stage_entered_at), reason_code: null, note: `Recycled (${r.outcome.reason_code})` });
    r.stage = "new";
    r.stage_entered_at = today();
    r.outcome = null;
    if (r.owner_id) r.assignments.unshift({ id: historySeq++, from: staffRow(r.owner_id)?.name, to: null, method: "pool", reason: "Recycled to the pool", by: "System", at: today() });
    r.owner_id = null;
    r.pool_reason = "recycled";
    r.recycled_count++;
    return ok(detail(r), "Back in the pool as New, with its earlier attempt kept in the history.");
  }],
  ["post", "tenant/prospects/:id/claim", ({ params }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (r.owner_id) return fail(`${r.code} already belongs to ${staffRow(r.owner_id)?.name}.`, null, 409);
    const me = staff.find((s) => s.id === ME);
    r.owner_id = me.id;
    r.team_id = me.team_id;
    r.pool_reason = null;
    r.assignments.unshift({ id: historySeq++, from: null, to: me.name, to_staff_id: me.id, team: teamRow(me.team_id)?.name, method: "claim", reason: "Claimed from the pool", by: me.name, at: today() });
    return ok(detail(r), `${r.code} is yours.`);
  }],
  ["get", "tenant/prospects/:id/suggestions", ({ params }) => (find(params.id) ? ok(suggestions(find(params.id))) : notFound("That prospect does not exist."))],

  // ---- The shared record engines, for prospects ----
  ["get", "tenant/records/prospect/:id/timeline", ({ params, query }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    const t = timeline(r);
    const kinds = [].concat(query.kinds ?? query["kinds[]"] ?? []);
    return ok(kinds.length ? { ...t, items: t.items.filter((i) => kinds.includes(i.kind)) } : t);
  }],
  ["get", "tenant/records/prospect/:id/stages", ({ params }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    const d = detail(r);
    return ok({ record: timeline(r).record, current: stage(r.stage), ageing: { stage: r.stage, name: stage(r.stage).name, days: d.ageing.days_in_stage, max_days: null, over: false, over_by: null }, allowed: d.allowed_stages, stages: meta().stages, history: d.history, outcomes: r.outcome ? [outcomeOf(r)] : [] });
  }],
  ["post", "tenant/records/prospect/:id/stage", ({ params, body }) => {
    const r = find(params.id);
    if (body.to === "converted") return fail("Convert it to a lead instead: the conversion creates the lead.", { code: "CRM_020" }, 422);
    if (body.to === "disqualified" && !body.reason_code) return fail("Before it moves to Disqualified, fill in: Reason code, Note.", { code: "CRM_020", details: { missing: ["Reason code", "Note"] } }, 422);
    const error = changeStage(r, body.to, body);
    if (error) return error;
    return ok({ stage: r.stage, history: r.history[0], ageing: { days: 0 } }, `Moved to ${stage(r.stage).name}.`);
  }],
  ["post", "tenant/records/prospect/:id/next-action", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (!body.type || !body.due_at) return invalid({ due_at: ["Say what happens next and when."] });
    setNextAction(r, body);
    return ok({ id: r.next_action.id, type: body.type, due_at: r.next_action.due_at, status: "open" }, "Next action set.");
  }],
  ["post", "tenant/records/prospect/:id/assign", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    const s = staff.find((x) => String(x.id) === String(body.owner_id));
    if (!s) return invalid({ owner_id: ["Choose who it goes to."] });
    if (r.owner_id === s.id) return fail(`It is already ${s.name}'s.`, null, 409);
    const a = { id: historySeq++, from: staffRow(r.owner_id)?.name ?? null, to: s.name, to_staff_id: s.id, team: teamRow(s.team_id)?.name, method: "manual", reason: body.reason ?? null, by: "Jane Wanjiku", at: today() };
    r.assignments.unshift(a);
    Object.assign(r, { owner_id: s.id, team_id: s.team_id, pool_reason: null });
    return ok(a, `Assigned to ${s.name}.`);
  }],
  ["get", "tenant/records/prospect/:id/assignments", ({ params }) => ok(find(params.id)?.assignments ?? [])],
  ["get", "tenant/records/prospect/:id/interactions", ({ params, query }) => ok(paginate((find(params.id)?.interactions ?? []).map((i) => ({ ...i, type_label: label(COMMS, i.type), logged_by: i.by })), query, 25))],
  ["get", "tenant/records/prospect/:id/notes", ({ params }) => ok(find(params.id)?.noteList ?? [])],
  ["post", "tenant/records/prospect/:id/notes", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    if (!body.body?.trim()) return invalid({ body: ["Write the note."] });
    const n = { id: NEXT_IDS.note++, body: body.body, visibility: body.visibility ?? "internal", pinned: false, author: "Jane Wanjiku", mine: true, created_at: today(), updated_at: today() };
    r.noteList.unshift(n);
    return created(n, "Note added.");
  }],
  ["get", "tenant/records/prospect/:id/attachments", ({ params }) => ok({
    attachments: find(params.id)?.attachments ?? [],
    document_types: [{ id: 4, code: "PROPOSAL", name: "Proposal" }, { id: 5, code: "QUOTATION", name: "Quotation" }],
    limits: { max_kb: 20480, mimes: ["pdf", "doc", "docx", "xls", "xlsx", "png", "jpg"] },
  })],
  ["post", "tenant/records/prospect/:id/attachments", ({ params, body }) => {
    const r = find(params.id);
    if (!r) return notFound("That prospect does not exist.");
    const a = { id: nextId(r.attachments), name: body.name || body.file?.name || "file", visibility: "internal", document_type: null, mime_type: null, size_bytes: body.file?.size ?? 0, ready: true, uploaded_by: "Jane Wanjiku", mine: true, created_at: today() };
    r.attachments.unshift(a);
    return created(a, "Attached. It can be downloaded once it has finished uploading.");
  }],
  ["get", "tenant/records/prospect/:id/attachments/:attachmentId/download", () => ok({ url: "#", filename: "file", expires_in: 300 })],
];
